import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, TrendingDown, Send, Sliders, Package, ShoppingCart, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtQty, UNITS, inr } from "./lib";
import StockAdjustDialog from "./components/StockAdjustDialog";
import EnquiryDialog from "./components/EnquiryDialog";

const unitLabel = (k) => (UNITS.find((u) => u.key === k) || { label: k || "" }).label;

/**
 * Reorder Alerts — one screen for the storekeeper to clear low/out-of-stock
 * items. One-click "Submit enquiry" and "Adjust stock" per row plus a batch
 * submit-all button at the top.
 */
export default function ReorderAlerts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [adjustPart, setAdjustPart] = useState(null);
  const [enquirySeed, setEnquirySeed] = useState(null); // { items, contextLabel }

  const load = () => {
    setLoading(true);
    api.get("/spares/parts/reorder-alerts")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectAll = (checked) => setSelected(checked ? Object.fromEntries(rows.map((r) => [r.id, true])) : {});

  const selectedRows = rows.filter((r) => selected[r.id]);
  const allChecked = rows.length > 0 && selectedRows.length === rows.length;

  const openBulkEnquiry = () => {
    const src = selectedRows.length > 0 ? selectedRows : rows;
    if (!src.length) { toast.info("Nothing to enquire — you're all stocked up."); return; }
    setEnquirySeed({
      items: src.map((r) => ({
        part_name: r.name,
        part_number: r.part_number || "",
        brand: r.brand || "",
        spec: [r.vehicle_make, r.vehicle_model, r.variant].filter(Boolean).join(" "),
        qty: r.suggested_qty || Math.max(1, Math.round((r.reorder_level || 1) * 1.5)),
        notes: "",
      })),
      contextLabel: `Reorder alerts — ${src.length} part${src.length === 1 ? "" : "s"}`,
    });
  };

  const openSingleEnquiry = (r) => {
    setEnquirySeed({
      items: [{
        part_name: r.name,
        part_number: r.part_number || "",
        brand: r.brand || "",
        spec: [r.vehicle_make, r.vehicle_model, r.variant].filter(Boolean).join(" "),
        qty: r.suggested_qty || Math.max(1, Math.round((r.reorder_level || 1) * 1.5)),
        notes: "",
      }],
      contextLabel: `Reorder for ${r.name}${r.part_number ? ` (${r.part_number})` : ""}`,
    });
  };

  return (
    <div className="space-y-4" data-testid="spare-reorder-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/spare-parts" data-testid="spare-reorder-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Reorder Alerts</h1>
            <p className="text-[12.5px] text-[#667085] mt-0.5">
              Parts sitting at or below their reorder level. Send an enquiry or adjust stock in one click.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={openBulkEnquiry}
                  data-testid="spare-reorder-submit-all"
                  disabled={rows.length === 0}
                  className="h-10 px-4 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <Send size={13} /> Submit {selectedRows.length > 0 ? `${selectedRows.length} selected` : "all as enquiry"}
          </button>
        </div>
      </header>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading alerts…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="spare-reorder-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#0F9256] mb-3">
              <Package size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">You&apos;re all stocked up</div>
            <p className="text-[12px] text-[#667085] mt-1">No parts have crossed their reorder level.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="spare-reorder-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={allChecked}
                           onChange={(e) => selectAll(e.target.checked)}
                           data-testid="spare-reorder-select-all"
                           className="w-4 h-4 accent-[#e63946]" />
                  </th>
                  <th className="px-3 py-2.5">Part</th>
                  <th className="px-3 py-2.5">Brand</th>
                  <th className="px-3 py-2.5 text-right">Available</th>
                  <th className="px-3 py-2.5 text-right">Reorder at</th>
                  <th className="px-3 py-2.5 text-right">Suggested</th>
                  <th className="px-3 py-2.5">Selling</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOut = (r.available_qty ?? 0) <= 0;
                  return (
                    <tr key={r.id} className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC]"
                        data-testid={`spare-reorder-row-${r.id}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={!!selected[r.id]} onChange={() => toggle(r.id)}
                               data-testid={`spare-reorder-select-${r.id}`}
                               className="w-4 h-4 accent-[#e63946]" />
                      </td>
                      <td className="px-3 py-3">
                        <Link to={`/spare-parts/stock/${r.id}`}
                              className="text-[13px] font-semibold text-[#1d3557] hover:text-[#e63946]">
                          {r.name}
                        </Link>
                        {r.part_number && <div className="text-[11px] font-mono text-[#667085]">{r.part_number}</div>}
                      </td>
                      <td className="px-3 py-3 text-[#667085]">{r.brand || "—"}</td>
                      <td className={`px-3 py-3 text-right font-mono tabular-nums font-bold ${isOut ? "text-[#e63946]" : "text-[#B54708]"}`}>
                        {fmtQty(r.available_qty, unitLabel(r.unit))}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-[#667085]">
                        {fmtQty(r.reorder_level, unitLabel(r.unit))}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums font-bold text-[#457b9d]">
                        {r.suggested_qty ? fmtQty(r.suggested_qty, unitLabel(r.unit)) : "—"}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums">{inr(r.selling_price)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button onClick={() => setAdjustPart(r)}
                                data-testid={`spare-reorder-adjust-${r.id}`}
                                className="h-7 px-2 rounded-md border border-[#E4E7EC] text-[11px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] mr-1 inline-flex items-center gap-1">
                          <Sliders size={11} /> Adjust
                        </button>
                        <button onClick={() => openSingleEnquiry(r)}
                                data-testid={`spare-reorder-enquiry-${r.id}`}
                                className="h-7 px-2 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[11px] font-semibold inline-flex items-center gap-1">
                          <Send size={11} /> Enquire
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <StockAdjustDialog
        open={!!adjustPart} part={adjustPart}
        onClose={() => setAdjustPart(null)}
        onSaved={() => { setAdjustPart(null); load(); }}
      />
      <EnquiryDialog
        open={!!enquirySeed}
        onClose={() => setEnquirySeed(null)}
        onSubmitted={() => { setEnquirySeed(null); setSelected({}); }}
        initialItems={enquirySeed?.items || []}
        contextLabel={enquirySeed?.contextLabel}
      />
    </div>
  );
}
